// ---------------------------------------------------------------------------
// QualityControlTab (US5 / PRODOPSAI-398)
//
// Replaces the Phase-0 `<EmptyTabPlaceholder />` mounted in the
// `quality_control` slot of `ManagementViewPage`. Fetches all governance
// matrices in one round-trip (US2 bulk endpoint) and renders one `MatrixCard`
// per entry in the response envelope, in registry order.
//
// Pure consumer of registry-driven specs:
//   - Title, subtitle, legend chips for each matrix come from `IMatrixCardSpec`
//     shipped by the backend. This component contributes ZERO per-metric copy
//     — admin edits in `/admin/metric-definitions` flow through automatically.
//   - The component injects only one cross-cutting callback (`onCellClick`)
//     into the inner matrix spec; everything else passes through unchanged.
//
// Phase 1 scope reminder (2026-05-09): three matrices ship — Status Count,
// Issue-Type Compliance, Epic Linkage. Incident Logging Coverage is descoped
// (PRODOPSAI-394 closed); `DoraReliabilityBanner` is NOT mounted in this tab.
// The banner stays exported in `@/components/management-view/` for Phase 3.
//
// The 4-state render mirrors `metric-view/TrendCard.tsx:42-55` — one
// `if`-branch per state (loading / error / empty / data), no overlapping
// conditions.
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBanner, Spinner } from '@/components/ui';
import type {
  IJiraSyncHealth,
  IMatrixCardSpec,
  IQualityControlMatricesResponse,
} from '@skystone/shared';
import { useManagementViewFilters } from '@/hooks/use-management-view-filters';
import { useQualityControlMatrices } from '@/hooks/use-quality-control-matrices';
import { buildQcDeepLink } from '@/lib/management-view/qc-deeplink';
import { MatrixCard } from './MatrixCard';
import { MatrixCardEmptyScope } from './MatrixCardEmptyScope';

/**
 * Which matrix card receives the Jira sync-health dot. Phase 1 wires it for
 * Status Count only — the dot indicates whether the underlying Jira status
 * data is trustworthy, which is the same signal Status Count surfaces. Other
 * QC matrices (Issue-Type Compliance, Epic Linkage) intentionally pass `undefined`
 * so the dot is omitted there.
 */
const SYNC_HEALTH_METRIC_KEY = 'statusCount';

/**
 * Return the `syncHealth` value only for the metric that should display the
 * dot — all other metrics receive `undefined` so `<JiraSyncHealthDot />`
 * renders nothing.
 */
function syncHealthForMetric(
  metricKey: string,
  syncHealth: IJiraSyncHealth | undefined,
): IJiraSyncHealth | undefined {
  if (metricKey === SYNC_HEALTH_METRIC_KEY) return syncHealth;
  return undefined;
}

// ---------------------------------------------------------------------------
// Module-scope helpers — defined outside the render body to satisfy SonarCloud
// rule S7721 (no helper functions inside a component render).
// ---------------------------------------------------------------------------

/**
 * True when a matrix has no team rows AND no org-row aggregate — i.e. the
 * active scope yielded zero data. The card still renders chrome but the body
 * shows an empty-scope placeholder rather than an empty `<table>`.
 */
function hasNoData(spec: IMatrixCardSpec): boolean {
  return spec.matrix.rows.length === 0 && !spec.matrix.orgRow;
}

// ---------------------------------------------------------------------------
// State branches — one per render branch, each kept tiny and ordered exactly
// like `TrendCard.tsx:42-55` so the canonical 4-state pattern is obvious.
// ---------------------------------------------------------------------------

function LoadingBranch(): JSX.Element {
  return (
    <div
      className="flex h-40 items-center justify-center"
      data-testid="quality-control-loading"
    >
      <Spinner size="lg" />
    </div>
  );
}

interface ErrorBranchProps {
  message: string;
}

function ErrorBranch({ message }: Readonly<ErrorBranchProps>): JSX.Element {
  return (
    <div data-testid="quality-control-error">
      <ErrorBanner message={message} />
    </div>
  );
}

function EmptyDataBranch(): JSX.Element {
  return (
    <p
      className="py-8 text-center text-sm text-muted-foreground"
      data-testid="quality-control-empty-data"
    >
      No quality-control metrics available for the active scope.
    </p>
  );
}

interface MatricesBranchProps {
  matrices: IMatrixCardSpec[];
  syncHealth: IJiraSyncHealth | undefined;
}

/**
 * Render the three matrix cards in registry order. For each card, choose
 * `MatrixCardEmptyScope` (chrome + empty-scope placeholder) when the spec has
 * no rows and no org-row; otherwise pass through to `MatrixCard`.
 *
 * `syncHealth` is forwarded only to the Status Count card via
 * `syncHealthForMetric` — see US5 / PRODOPSAI-430.
 */
function MatricesBranch({ matrices, syncHealth }: Readonly<MatricesBranchProps>): JSX.Element {
  return (
    <div className="space-y-6" data-testid="quality-control-matrices">
      {matrices.map((spec) =>
        hasNoData(spec)
          ? <MatrixCardEmptyScope key={spec.metricKey} spec={spec} />
          : (
            <MatrixCard
              key={spec.metricKey}
              spec={spec}
              syncHealth={syncHealthForMetric(spec.metricKey, syncHealth)}
            />
          ),
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function QualityControlTab(): JSX.Element {
  const { scope, window: windowFilter, cadence, includeArchived } = useManagementViewFilters();
  const { data, loading, error } = useQualityControlMatrices({
    scope,
    window: windowFilter,
    cadence,
    includeArchived,
  });

  const navigate = useNavigate();

  // PRODOPSAI-648: a clicked cell deep-links to Metric View (Product Flow tab),
  // pre-scoped to the same team and the column's exact period.
  const onCellClick = useCallback(
    (e: { rowId: string; columnIndex: number; value: number | null }, columnPeriods?: { start: string; end: string }[]) => {
      navigate(buildQcDeepLink({
        teamId: e.rowId,
        period: columnPeriods?.[e.columnIndex],
      }));
    },
    [navigate],
  );

  // Inject the cell-click callback into each matrix spec. Memoised on the
  // response identity (and the stable `onCellClick`) so MatrixCard instances do
  // not re-render with a fresh `onCellClick` reference on every parent render.
  const enhanced = useMemo(() => buildEnhancedSpecs(data, onCellClick), [data, onCellClick]);

  if (loading) return <LoadingBranch />;
  if (error) return <ErrorBranch message={error} />;
  if (enhanced.length === 0) return <EmptyDataBranch />;
  return <MatricesBranch matrices={enhanced} syncHealth={data?.syncHealth} />;
}

/**
 * Map the response envelope into a list of `IMatrixCardSpec`, injecting an
 * `onCellClick` that deep-links each cell to Metric View (Product Flow tab)
 * for the clicked team + period (PRODOPSAI-648). Returns `[]` when the response
 * is `null` or its `matrices` array is empty — both are valid states the tab
 * handles via `EmptyDataBranch`.
 *
 * The matrix only fires `onCellClick` for non-org cells, so the deep-link
 * always carries a real team id (Product Flow's Workflow Hygiene needs one).
 */
function buildEnhancedSpecs(
  data: IQualityControlMatricesResponse | null,
  onCellClick: (
    e: { rowId: string; columnIndex: number; value: number | null },
    columnPeriods?: { start: string; end: string }[],
  ) => void,
): IMatrixCardSpec[] {
  if (data === null) return [];
  return data.matrices.map((spec) => ({
    ...spec,
    matrix: {
      ...spec.matrix,
      onCellClick: (e) => onCellClick(e, spec.matrix.columnPeriods),
    },
  }));
}

export default QualityControlTab;
